import { useEffect, useState } from "react";
import { X, Share, Plus, MoreVertical, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { detectPlatform, isStandalone, type Platform } from "@/lib/pwa";

const DISMISS_KEY = "ms_install_banner_dismissed_v1";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function InstallBanner() {
  const [show, setShow] = useState(false);
  const [platform, setPlatform] = useState<Platform>("other");
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;

    const p = detectPlatform();
    setPlatform(p);

    // Only auto-show on mobile (iOS / Android)
    if (p !== "ios" && p !== "android") return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // Delay showing so it doesn't fight with first paint
    const t = setTimeout(() => setShow(true), 1500);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      clearTimeout(t);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
  };

  const handleAndroidInstall = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") dismiss();
      setDeferredPrompt(null);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-[60] p-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pointer-events-none">
      <div className="pointer-events-auto mx-auto max-w-md rounded-2xl border border-border bg-card text-card-foreground shadow-2xl overflow-hidden">
        <div className="flex items-start gap-3 p-4">
          <div className="flex-shrink-0 h-10 w-10 rounded-xl overflow-hidden bg-background">
            <img src="/pwa-192.png" alt="" className="h-full w-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm">Install Microsports</h3>
            {platform === "ios" ? (
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Tap{" "}
                <Share className="inline h-3.5 w-3.5 align-text-bottom mx-0.5" />{" "}
                <span className="font-medium">Share</span> in Safari, then{" "}
                <span className="font-medium">Add to Home Screen</span>{" "}
                <Plus className="inline h-3.5 w-3.5 align-text-bottom mx-0.5" />
              </p>
            ) : (
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                {deferredPrompt
                  ? "Install for a faster, app-like experience and push notifications."
                  : (
                    <>
                      Tap{" "}
                      <MoreVertical className="inline h-3.5 w-3.5 align-text-bottom mx-0.5" />{" "}
                      menu in Chrome, then{" "}
                      <span className="font-medium">Install app</span> /{" "}
                      <span className="font-medium">Add to Home screen</span>.
                    </>
                  )}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={dismiss}
            className="h-7 w-7 -mr-1 -mt-1 flex-shrink-0"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        {platform === "android" && deferredPrompt && (
          <div className="px-4 pb-4 pt-0">
            <Button
              onClick={handleAndroidInstall}
              size="sm"
              className="w-full gap-2"
            >
              <Download className="h-4 w-4" />
              Install app
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
